/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { forwardRef, Fragment, memo, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';

// Component imports
import { Card, Col, Collapse, Divider, Empty, Input, Row, Space, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

// Misc imports
import { DEFAULT_NODE_SUBCATEGORY, NODE_CATEGORIES, NODE_TYPES_PROPERTIES } from '../../constants/flowGraph';
import { GraphContext } from './FlowGraph';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { t } from 'i18next';
import { isEmptyObject } from '../../utils/is/isEmptyObject';
import { isEmptyArray } from '../../utils/is/isEmptyArray';

// Constant declarations
const baseClassName = 'conversation-graph__add-node-panel';

const AddNodePanel = forwardRef((props, ref) => {
  const { addNewElements, openEditForm, disabledNodeTypes, closeAddNewNodePanel } = useContext(GraphContext);
  const { primaryBotLanguage } = useBotLanguage();
  const [searchValue, setSearchValue] = useState('');
  const [activeCollapsedKeys, setActiveCollapsedKeys] = useState([]);
  const searchInput = useRef(null);
  const panelRef = useRef(null);

  useImperativeHandle(ref, () => ({
    clearSearchInput: () => setSearchValue(''),
    getPanelElement: () => panelRef.current
  }));

  useEffect(() => {
    if (searchInput.current) {
      searchInput.current.focus();
    }
  }, []);

  /*
      Group Nodes first by any categories they have. eg Message node can be grouped both in Send Message category and Popular
      And secondly by any sub category they have. e.g Ask user category is further categorized into 2 sub categories
      For consistency reasons we put a default subcategory if no specific subcategory is set
      If search value exists then filter accordingly based on label and prune categories or subcategories that are empty
    */
  const groupNodesByCategoryAndSubCategoryBasedOnSearchValue = (searchValue) => {
    const lowerCaseSearchValue = searchValue.toLowerCase();

    const groupedNodes = Object.values(NODE_TYPES_PROPERTIES).reduce((acc, node) => {
      // Exclude nodes that are hidden or disabled
      if (node.hidden || disabledNodeTypes.includes(node.type)) {
        return acc;
      }

      const currentNodeCategories = node.categories || [];
      const currentNodeSubCategory = node.subCategory || DEFAULT_NODE_SUBCATEGORY;

      currentNodeCategories.forEach(category => {
        if (!acc[category.key]) {
          acc[category.key] = {};
        }

        const subCategory = category.key === NODE_CATEGORIES.POPULAR.key
          ? DEFAULT_NODE_SUBCATEGORY
          : currentNodeSubCategory;

        if (!acc[category.key][subCategory]) {
          acc[category.key][subCategory] = [];
        }

        // If there's no search value, include all nodes,
        // else include only those whose label matches the search
        if (!searchValue || (node.label && node.label.toLowerCase().includes(lowerCaseSearchValue))) {
          acc[category.key][subCategory].push(node);
        }
      });

      return acc;
    }, {});

    // Delete empty categories and subcategories
    Object.entries(groupedNodes).forEach(([category, subCategories]) => {
      Object.entries(subCategories).forEach(([subCategory, nodes]) => {
        if(isEmptyArray(nodes)) {
          delete groupedNodes[category][subCategory];
        }
        if(isEmptyObject(groupedNodes[category])) {
          delete groupedNodes[category];
        }

        if(searchValue) {
          delete groupedNodes[NODE_CATEGORIES.POPULAR.key];
        }
      });
    });

    return groupedNodes;
  };

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const onAddNode = (nodeType) => {
    let newNode = {
      id: uuidv4(),
      type: nodeType,
      data: {
        label: { [primaryBotLanguage]: '' },
        nodeType: 'node'
      }
    };

    addNewElements({ nodes: [newNode] });
    setTimeout(() => {
      setSearchValue('');
      closeAddNewNodePanel();
      openEditForm(newNode.id);
    }, 0);
  };

  const filteredGroupedNodes = useMemo(() => {
    return groupNodesByCategoryAndSubCategoryBasedOnSearchValue(searchValue);
  }, [searchValue]);

  useEffect(() => {

    if(!!searchValue && filteredGroupedNodes) {
      setActiveCollapsedKeys(Object.entries(filteredGroupedNodes).map(([category, _]) => category));
    }

  }, [searchValue, filteredGroupedNodes]);
  return (
    <Fragment>
      <Card size="small" className={baseClassName} ref={panelRef}>
        <Row>
          <Col xs={24}>
            <Space className="flex-wrap mb-2">
              <Input
                ref={searchInput}
                size="large"
                prefix={
                  <SearchOutlined
                    className="text-secondary"
                  />
                }
                value={searchValue}
                placeholder={t('Search nodes')}
                allowClear
                onChange={(event) => handleSearch(event.target.value)}
              />
            </Space>
          </Col>
          <Col xs={24}>
            {
              isEmptyObject(filteredGroupedNodes) ? (
                <Empty
                  className="text-muted p-2"
                  description="No matching nodes found. Try a different search term."
                />
              ) : (
                <Space direction="vertical" size={0} split={<Divider />}>
                  <Collapse
                    ghost
                    {...(!!searchValue && activeCollapsedKeys?.length > 0 && {
                      activeKey: activeCollapsedKeys
                    })}
                    defaultActiveKey={NODE_CATEGORIES.POPULAR.key}
                    destroyInactivePanel
                    accordion={!searchValue}
                    className="ant-collapse-content-box-p-0 ant-collapse-header-px-0"
                  >
                    {
                      Object.entries(filteredGroupedNodes).sort((a, b) => {
                        const aPriority = Object.values(NODE_CATEGORIES).find(category => category.key === a[0]).priority;
                        const bPriority = Object.values(NODE_CATEGORIES).find(category => category.key === b[0]).priority;
                        return aPriority - bPriority;
                      }).map(([category, subCategories], index) => {
                        return (
                          <Collapse.Panel
                            className={classNames('border-radius-0', {
                              'border-bottom': index !== Object.keys(filteredGroupedNodes).length - 1
                            })}
                            key={category}
                            header={
                              <Typography.Text
                                onClick={() => {
                                  if(!searchValue) {
                                    return;
                                  }
                                  setActiveCollapsedKeys(prev => {
                                    if(prev.includes(category)) {
                                      return prev.filter(prevItem => prevItem !== category);
                                    } else {
                                      return [...prev, category];
                                    }
                                  });
                                }
                                }
                                className="text-muted fw-500"
                              >
                                {t(category, { context: 'node category' })?.toLocaleUpperCase?.()}
                              </Typography.Text>
                            }
                          >
                            {
                              Object.entries(subCategories).map(([subCategory, nodes]) => {
                                return (
                                  <Fragment>
                                    <Space direction="vertical" size={0} className="pb-2">
                                      {
                                        subCategory !== DEFAULT_NODE_SUBCATEGORY && (
                                          <Space className="pb-2">
                                            <Typography.Text
                                              className="text-muted fs-sm"
                                            >
                                              {t(subCategory, { context: 'node subcategory' })?.toLocaleUpperCase?.()}
                                            </Typography.Text>
                                          </Space>
                                        )
                                      }
                                      {
                                        nodes.map(node => (
                                          <Space
                                            className={`dndnode ${node.type} p-1`}
                                            onDragStart={(event) => onDragStart(event, node.type)}
                                            onClick={() => onAddNode(node.type)}
                                            draggable
                                            key={node.label}
                                          >
                                            <Tooltip
                                              title={node.info || ''}
                                              placement="right"
                                            >
                                              <Space size={10}>
                                                {node.icon}
                                                {node.label}
                                              </Space>
                                            </Tooltip>
                                          </Space>
                                        ))
                                      }
                                    </Space>
                                  </Fragment>
                                );
                              })
                            }
                          </Collapse.Panel>
                        );
                      } )
                    }
                  </Collapse>
                </Space>
              )
            }

          </Col>
        </Row>
      </Card>
    </Fragment>

  );
});

export default memo(AddNodePanel);
